"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.listMetaSchema = exports.offsetSchema = exports.cursorSchema = exports.UNPAGED_LIST_CEILING = exports.MAX_PAGE_SIZE = exports.DEFAULT_PAGE_SIZE = void 0;
exports.listResponseSchema = listResponseSchema;
exports.pageSize = pageSize;
exports.pageOffset = pageOffset;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE LIST ENVELOPE. First used by worklogs, and it took until C12.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * CLAUDE.md §3: *"A single resource returns the resource; lists return `{ data, meta: {
 * next_cursor } }`. Cursor-based pagination, default 50, maximum 200."*
 *
 * ⚠️ **Every list before this one returned a bare `{ data }`, and each said why.** Clients and
 * projects are bounded by how many a company has; roles by how many a workspace defines; a task's
 * comments by how many people wrote. `delivery.controller.ts` named the trigger — *"the first list
 * that can grow without bound (tasks, C8; worklogs, C11) needs the envelope"* — and C12's
 * *"Minden rögzítés"* view is that list: one person's every worklog, for as long as they have
 * worked here. There is no window on it and no natural bound.
 *
 * ⚠️⚠️ **A KEYSET CURSOR, NEVER AN OFFSET, and `state-rules.md` §6 says so: *"never page numbers
 * over a keyset cursor"*.** An offset re-reads the same rows after an insert and skips one after a
 * delete — on a list somebody is actively adding to, which is exactly what a worklog list is.
 *
 * ⚠️⚠️ **AND THE SORT KEY MUST BE UNIQUE OR THE CURSOR SKIPS ROWS.** `worked_on` alone is not:
 * five entries on one day share it, so `worked_on < $cursor` drops the four the page boundary fell
 * between. Every cursor here is a **pair** — the ordering column plus the id — and the comparison
 * is a row comparison. This is the single most common way keyset pagination is written wrong, and
 * it is invisible until a page boundary happens to land inside a group of ties.
 *
 * ⚠️ **`hard-won.md` warns about cursors built from a timestamp** — one carries the process's
 * timezone, so the comparison never crosses a boundary and the cursor stops advancing. Not a
 * hazard here, and deliberately so: `worked_on` is a `date` and `id` is a uuid v7. Neither has a
 * zone. Any future cursor over a `timestamptz` needs that row read first.
 */
exports.DEFAULT_PAGE_SIZE = 50;
exports.MAX_PAGE_SIZE = 200;
/**
 * 30 Oct 2026 · the safety ceiling of a list that does NOT page — a workspace's teams, roles,
 * project roles, metafields, extensions. It was `MAX_PAGE_SIZE`, which made a vocabulary past 200
 * silently end with no `next_cursor` to say so; these are configured by hand and never near this.
 */
exports.UNPAGED_LIST_CEILING = 1000;
/**
 * The cursor a client sends back, opaque by construction.
 *
 * ⚠️ **Opaque because the client must not compose one.** It is `<sort value>|<id>` base64url'd —
 * readable in a debugger, not a documented shape. A client that built its own would be depending
 * on the server's ordering, and the day the ordering changes every saved link breaks silently.
 */
exports.cursorSchema = zod_1.z.string().min(1).max(256);
/**
 * The page number's other half, as a query parameter.
 *
 * ⚠️ **`coerce`, because a query string is text.** `?offset=100` arrives as `'100'`, and a plain
 * `z.number()` refuses it — which reads as a 400 on a link somebody pasted.
 *
 * ⚠️ **Optional and never required**: an absent offset is the first page, which is what a client
 * that has never heard of this parameter should get and does.
 */
exports.offsetSchema = zod_1.z.coerce.number().int().nonnegative();
exports.listMetaSchema = zod_1.z.object({
    /**
     * ⚠️ `null` on the last page. Kept beside the offset figures because a caller that only wants
     * *"is there more"* should not have to do the arithmetic.
     */
    next_cursor: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **THE TOTAL, AND IT COUNTS THE FILTERED SET — NOT THE TABLE.** A pager reporting 1,347
     * while showing the 12 rows that survived a filter is worse than one reporting nothing:
     * `state-rules.md` §5 forbids presenting a figure derived from a partial fetch as a total, and a
     * count of the wrong population is that failure with a bigger number on it.
     */
    total: zod_1.z.number().int().nonnegative(),
    /** How many rows were skipped to produce this page. */
    offset: zod_1.z.number().int().nonnegative(),
    /** How many were asked for — the clamped value, not the requested one. */
    limit: zod_1.z.number().int().positive(),
});
/**
 * ⚠️⚠️ **THERE IS A TOTAL NOW, AND THIS RECORDS WHAT IT COST.** The note that stood here refused
 * one on two grounds, and both were correct: a `count(*)` over the filtered set is a second scan on
 * every page, and the figure is stale the moment it is read. The product owner's ruling of
 * 9 Sep 2026 (`qol-fixes.md` D1) overrules them, for a reason neither addressed — the pager the
 * feedback asks for is `[<] [selector] [>] [total]`, and a jump to page seven cannot be expressed
 * with a cursor at all. A cursor answers *what comes after this row*; it cannot answer *what comes
 * after 300 rows I have not read*.
 *
 * ⚠️ **The staleness is the smaller problem and is accepted.** A total read one statement before
 * the page may be a row out by the time it renders, which is what every offset pager does; the
 * alternative on offer was no total at all.
 *
 * ⚠️ **The second scan is bounded by the same `where` clause as the page**, so it costs what the
 * filter costs rather than what the table costs. A screen wanting a SUM still asks for the
 * aggregate — `project.trackedHours`, `task.trackedHours`, the worklog overview's `weekHours` —
 * because `state-rules.md` §5 has not moved: a figure derived from a partial fetch is still never
 * presented as a total.
 */
function listResponseSchema(item) {
    return zod_1.z.object({ data: zod_1.z.array(item), meta: exports.listMetaSchema });
}
/**
 * ⚠️⚠️ **THE ENCODE/DECODE PAIR IS NOT HERE, AND `Buffer` IS THE REASON.** This module is bundled
 * into the browser — `apps/web` imports `listResponseSchema` from it — and `Buffer` is a Node
 * global that does not exist there. A `Buffer.from` inside a function body typechecks against
 * `@types/node`, survives the bundle, and throws the first time a page is loaded, which is the
 * worst of the three places that could have failed.
 *
 * It also should not be here on the merits: **the client never composes or reads a cursor.** It
 * receives an opaque string and sends it back. Encoding lives in `apps/api/src/cursor.ts`, next to
 * the only code that has any business knowing the shape.
 */
/**
 * Clamp a requested page size.
 *
 * ⚠️ **Clamped, not refused.** `?limit=5000` is a client asking for too much, not an attack and
 * not a bug worth a 400 — it gets 200 rows and a cursor, which is the answer to what it wanted.
 * A rejection here would make an honest client's retry loop the failure mode.
 */
function pageSize(requested) {
    if (requested === undefined)
        return exports.DEFAULT_PAGE_SIZE;
    const value = typeof requested === 'string' ? Number(requested) : requested;
    if (!Number.isFinite(value) || value < 1)
        return exports.DEFAULT_PAGE_SIZE;
    return Math.min(exports.MAX_PAGE_SIZE, Math.floor(value));
}
/**
 * Clamp a requested offset.
 *
 * ⚠️ **Clamped at zero and nothing else.** An offset past the end is not an error — it is a page
 * that has emptied since the link was made, and the honest answer is no rows and a `total` that
 * says why. Refusing it would turn a stale bookmark into a 400.
 */
function pageOffset(requested) {
    if (requested === undefined)
        return 0;
    const value = typeof requested === 'string' ? Number(requested) : requested;
    if (!Number.isFinite(value) || value < 0)
        return 0;
    return Math.floor(value);
}
//# sourceMappingURL=pagination.js.map